import { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../../../contexts/AuthContext';
import { useToast } from '../../../../contexts/ToastContext';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { utcToDisplayDateAndTime, displayDateTimeToUTC } from '../../../../utils/timezoneUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

// Use same-origin API by default in browsers; allow override via REACT_APP_API_URL for local/dev tools
const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Custom hook for managing Crew Duty Roster event handlers
 * @param {Function} fetchEntries
 * @param {Function} setEntries
 * @param {string} [displayTimezone] - IANA timezone for display/input (e.g. Asia/Kolkata). When set, duty times are entered and shown in this zone and stored as UTC.
 */
export const useCrewDutyRosterHandlers = (fetchEntries, setEntries, displayTimezone = 'Asia/Kolkata') => {
  const { user } = useAuth();
  const { showToast } = useToast();
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [formData, setFormData] = useState({
    crewId: '',
    dutyType: 'FLIGHT',
    trainingCategory: 'GROUND_TRAINING',
    trainingAircraftId: '',
    dutyStartTime: null,
    dutyEndTime: null,
    remarks: '',
    nonComplianceJustification: '',
    compensatoryRestHours: '',
    restFacility: '',
    flightTimeTable: 'AUTO',
    recordedFlightTimeHours: '',
    // Helicopter-specific fields
    sectorId: null,
    tripId: null,
    aircraftType: null,
    crewComplement: 'SINGLE_PILOT',
    numberOfLandings: 0,
    hasPositioning: false,
    positioningStartTime: null,
    positioningEndTime: null,
    isSplitDuty: false,
    splitDutyBreakStart: null,
    splitDutyBreakEnd: null,
    splitDutyAccommodationProvided: false,
    rosterPlanOnly: false,
    dutyWorkLocationCity: '',
    dutyWorkLocationCode: ''
  });
  const [aircraftType, setAircraftType] = useState('Fixed Wing');
  const [complianceWarnings, setComplianceWarnings] = useState([]);
  const [complianceViolations, setComplianceViolations] = useState([]);
  const [complianceContext, setComplianceContext] = useState(null);
  const [partIiiEligible, setPartIiiEligible] = useState(false);
  const [requiredCompensatoryRestHours, setRequiredCompensatoryRestHours] = useState(0);
  const [hardBlockMessages, setHardBlockMessages] = useState([]);
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);

  const handleCreate = () => {
    setSelectedEntry(null);
    setFormData({
      crewId: '',
      dutyType: 'FLIGHT',
      trainingCategory: 'GROUND_TRAINING',
      trainingAircraftId: '',
      dutyStartTime: null,
      dutyEndTime: null,
      remarks: '',
      nonComplianceJustification: '',
      compensatoryRestHours: '',
      restFacility: '',
      flightTimeTable: 'AUTO',
      recordedFlightTimeHours: '',
      // Helicopter-specific fields
      sectorId: null,
      tripId: null,
      aircraftType: null,
      crewComplement: 'SINGLE_PILOT',
      numberOfLandings: 0,
      hasPositioning: false,
      positioningStartTime: null,
      positioningEndTime: null,
      isSplitDuty: false,
      splitDutyBreakStart: null,
      splitDutyBreakEnd: null,
      splitDutyAccommodationProvided: false,
      rosterPlanOnly: false,
      dutyWorkLocationCity: '',
      dutyWorkLocationCode: ''
    });
    setAircraftType('Fixed Wing');
    setComplianceWarnings([]);
    setComplianceViolations([]);
    setDialogOpen(true);
  };

  const handleCreateFromMismatch = (mismatchItem) => {
    setSelectedEntry(null);

    let dutyStart = null;
    let dutyEnd = null;
    if (mismatchItem.departure) {
      const depDate = mismatchItem.departure;
      const depTime = mismatchItem.departureTime || '00:00';
      const dep = dayjs.tz(`${depDate}T${depTime}`, displayTimezone);
      dutyStart = dep.subtract(30, 'minute');
    }
    if (mismatchItem.arrival) {
      const arrDate = mismatchItem.arrival;
      const arrTime = mismatchItem.arrivalTime || '00:00';
      const arr = dayjs.tz(`${arrDate}T${arrTime}`, displayTimezone);
      dutyEnd = arr.add(15, 'minute');
    }

    const routeInfo = mismatchItem.route || '';
    const charterInfo = mismatchItem.charterNumber || mismatchItem.tripTitle || '';
    const remarks = [charterInfo, routeInfo].filter(Boolean).join(' — ');

    setFormData({
      crewId: mismatchItem.crewId || '',
      dutyType: 'FLIGHT',
      trainingCategory: 'GROUND_TRAINING',
      trainingAircraftId: '',
      dutyStartTime: dutyStart,
      dutyEndTime: dutyEnd,
      remarks,
      nonComplianceJustification: '',
      compensatoryRestHours: '',
      restFacility: '',
      flightTimeTable: 'AUTO',
      recordedFlightTimeHours: '',
      sectorId: mismatchItem.sectorId || null,
      tripId: mismatchItem.tripId || null,
      aircraftType: null,
      crewComplement: 'SINGLE_PILOT',
      numberOfLandings: 0,
      hasPositioning: false,
      positioningStartTime: null,
      positioningEndTime: null,
      isSplitDuty: false,
      splitDutyBreakStart: null,
      splitDutyBreakEnd: null,
      splitDutyAccommodationProvided: false,
      rosterPlanOnly: false,
      dutyWorkLocationCity: '',
      dutyWorkLocationCode: ''
    });

    const acType = mismatchItem.aircraftType || 'Helicopter';
    setAircraftType(acType);
    setComplianceWarnings([]);
    setComplianceViolations([]);
    setDialogOpen(true);
  };

  const handleViewDutyFromCalendar = async (dutyId) => {
    try {
      const response = await axios.get(`${API_URL}/ops/crew-duty-roster/${dutyId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      handleEdit(response.data);
    } catch (error) {
      console.error('Error fetching duty entry:', error);
      showToast('Failed to fetch duty entry details', 'error');
    }
  };

  const toDisplayTzDayjs = (utcDate) => {
    if (!utcDate || !displayTimezone) return null;
    const { date: d, time: t } = utcToDisplayDateAndTime(utcDate, displayTimezone);
    if (!d) return null;
    return dayjs.tz(`${d}T${t}`, displayTimezone);
  };

  const handleEdit = (entry) => {
    setSelectedEntry(entry);
    const sectorId = entry.sectorId?._id || entry.sectorId || null;
    const tripId = entry.tripId?._id || entry.tripId || null;
    setFormData({
      crewId: entry.crewId._id || entry.crewId,
      dutyType: entry.dutyType,
      trainingCategory: entry.dutyType === 'TRAINING' ? (entry.trainingCategory || 'GROUND_TRAINING') : 'GROUND_TRAINING',
      trainingAircraftId: entry.trainingAircraftId ? (entry.trainingAircraftId._id || entry.trainingAircraftId) : '',
      dutyStartTime: toDisplayTzDayjs(entry.dutyStartTime),
      dutyEndTime: toDisplayTzDayjs(entry.dutyEndTime),
      remarks: entry.remarks || '',
      nonComplianceJustification: entry.nonComplianceJustification || '',
      compensatoryRestHours: entry.compensatoryRestHours || '',
      restFacility: entry.restFacility || '',
      flightTimeTable: entry.flightTimeTable || 'AUTO',
      recordedFlightTimeHours: entry.recordedFlightTimeHours ?? '',
      // Helicopter-specific fields
      sectorId: sectorId ? String(sectorId) : null,
      tripId: tripId ? String(tripId) : null,
      aircraftType: entry.aircraftType || null,
      crewComplement: entry.crewComplement || 'SINGLE_PILOT',
      numberOfLandings: entry.numberOfLandings || 0,
      hasPositioning: entry.hasPositioning || false,
      positioningStartTime: toDisplayTzDayjs(entry.positioningStartTime),
      positioningEndTime: toDisplayTzDayjs(entry.positioningEndTime),
      isSplitDuty: entry.isSplitDuty || false,
      splitDutyBreakStart: toDisplayTzDayjs(entry.splitDutyBreakStart),
      splitDutyBreakEnd: toDisplayTzDayjs(entry.splitDutyBreakEnd),
      splitDutyAccommodationProvided: entry.splitDutyAccommodationProvided || false,
      rosterPlanOnly: entry.rosterPlanOnly === true,
      dutyWorkLocationCity: entry.dutyWorkLocationCity || '',
      dutyWorkLocationCode: entry.dutyWorkLocationCode || ''
    });
    setAircraftType(entry.aircraftType || formData.aircraftType || 'Fixed Wing');
    setComplianceWarnings([]);
    setComplianceViolations([]);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      // Validate required fields
      if (!formData.crewId) {
        showToast('Please select a crew member', 'error');
        return;
      }
      if (!formData.dutyStartTime) {
        showToast('Please select a duty start time', 'error');
        return;
      }
      if (!formData.dutyEndTime) {
        showToast('Please select a duty end time', 'error');
        return;
      }
      if (formData.dutyStartTime && formData.dutyEndTime && formData.dutyStartTime.isAfter(formData.dutyEndTime)) {
        showToast('Duty start time must be before end time', 'error');
        return;
      }

      // Ensure dates are valid and convert to ISO8601 strings (form values are in display timezone)
      if (!formData.dutyStartTime || !formData.dutyStartTime.isValid()) {
        showToast('Invalid duty start time', 'error');
        return;
      }
      if (!formData.dutyEndTime || !formData.dutyEndTime.isValid()) {
        showToast('Invalid duty end time', 'error');
        return;
      }

      if (formData.isSplitDuty) {
        if (!formData.splitDutyBreakStart?.isValid?.() || !formData.splitDutyBreakEnd?.isValid?.()) {
          showToast('Split duty requires both break start and break end times', 'error');
          return;
        }
        if (!formData.splitDutyBreakEnd.isAfter(formData.splitDutyBreakStart)) {
          showToast('Split duty break end must be after break start', 'error');
          return;
        }
        if (
          formData.splitDutyBreakStart.isBefore(formData.dutyStartTime) ||
          formData.splitDutyBreakEnd.isAfter(formData.dutyEndTime)
        ) {
          showToast('Split duty break must fall within the duty period', 'error');
          return;
        }
      }

      if (formData.dutyType === 'FLIGHT' && formData.rosterPlanOnly && !(formData.dutyWorkLocationCity || '').trim()) {
        showToast('Work-Duty Location (city) is required for roster plan flight entries', 'error');
        return;
      }

      const toUTC = (d) => {
        if (!d || !d.isValid()) return undefined;
        const iso = displayDateTimeToUTC(d.format('YYYY-MM-DD'), d.format('HH:mm'), displayTimezone);
        return iso || undefined;
      };

      const dutyStartTimeISO = toUTC(formData.dutyStartTime);
      const dutyEndTimeISO = toUTC(formData.dutyEndTime);
      if (!dutyStartTimeISO || !dutyEndTimeISO) {
        showToast('Invalid duty start or end time', 'error');
        return;
      }

      const payload = {
        crewId: formData.crewId,
        dutyType: formData.dutyType,
        dutyStartTime: dutyStartTimeISO,
        dutyEndTime: dutyEndTimeISO,
        remarks: formData.remarks,
        nonComplianceJustification: formData.nonComplianceJustification || undefined,
        compensatoryRestHours: formData.compensatoryRestHours !== '' && formData.compensatoryRestHours != null
          ? Number(formData.compensatoryRestHours)
          : undefined,
        restFacility: formData.restFacility || undefined,
        flightTimeTable: formData.flightTimeTable || undefined,
        recordedFlightTimeHours: formData.recordedFlightTimeHours !== '' && formData.recordedFlightTimeHours != null
          ? Number(formData.recordedFlightTimeHours)
          : undefined,
        aircraftType: aircraftType || undefined,
        trainingCategory: formData.dutyType === 'TRAINING' ? formData.trainingCategory : undefined,
        trainingAircraftId: formData.dutyType === 'TRAINING' && formData.trainingCategory === 'FLIGHT_TRAINING' && formData.trainingAircraftId ? formData.trainingAircraftId : undefined,
        // Always send sector/trip so Ops can link or clear a charter on update
        sectorId: formData.sectorId || null,
        tripId: formData.tripId || null,
        crewComplement: formData.crewComplement || undefined,
        numberOfLandings: formData.numberOfLandings != null ? formData.numberOfLandings : undefined,
        hasPositioning: formData.hasPositioning || undefined,
        positioningStartTime: formData.hasPositioning ? toUTC(formData.positioningStartTime) : undefined,
        positioningEndTime: formData.hasPositioning ? toUTC(formData.positioningEndTime) : undefined,
        isSplitDuty: formData.isSplitDuty === true,
        splitDutyBreakStart: formData.isSplitDuty ? toUTC(formData.splitDutyBreakStart) : undefined,
        splitDutyBreakEnd: formData.isSplitDuty ? toUTC(formData.splitDutyBreakEnd) : undefined,
        splitDutyBreakDuration: formData.isSplitDuty && formData.splitDutyBreakStart && formData.splitDutyBreakEnd
          ? formData.splitDutyBreakEnd.diff(formData.splitDutyBreakStart, 'minute') / 60
          : undefined,
        splitDutyAccommodationProvided: formData.splitDutyAccommodationProvided || undefined,
        rosterPlanOnly: formData.dutyType === 'FLIGHT' ? formData.rosterPlanOnly === true : undefined,
        ...(formData.dutyType === 'FLIGHT' && formData.rosterPlanOnly
          ? {
              dutyWorkLocationCity: (formData.dutyWorkLocationCity || '').trim(),
              dutyWorkLocationCode: (() => {
                const c = (formData.dutyWorkLocationCode || '').trim().toUpperCase().replace(/[^A-Z]/g, '');
                return c.slice(0, 4) || undefined;
              })()
            }
          : {})
      };

      const toastOffDayWarnings = (warnings) => {
        const offDayMsgs = (warnings || [])
          .filter((w) => w?.type === 'OFF_DAY_PATTERN_WARNING' || /off day/i.test(w?.message || w || ''))
          .map((w) => w.message || w);
        if (offDayMsgs.length) {
          showToast(offDayMsgs.join(' '), 'warning');
        }
      };

      if (selectedEntry) {
        const response = await axios.put(`${API_URL}/ops/crew-duty-roster/${selectedEntry._id}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        showToast('Duty roster entry updated successfully', 'success');
        toastOffDayWarnings(response.data?.warnings);
      } else {
        const response = await axios.post(`${API_URL}/ops/crew-duty-roster`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        // Check if response indicates violations requiring justification
        if (response.data.requiresJustification) {
          setComplianceViolations(response.data.violations || []);
          setComplianceWarnings(response.data.warnings || []);
          setComplianceContext(response.data.complianceContext || null);
          setOverrideDialogOpen(true);
          return;
        }

        showToast('Duty roster entry created successfully', 'success');
        toastOffDayWarnings(response.data?.warnings);
      }

      setDialogOpen(false);
      fetchEntries();
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 400 && data?.requiresJustification) {
        const hard = (data.hardViolations || []).map((v) => v.message || v);
        const partIii = (data.partIiiViolations || []).map((v) => v.message || v);
        const company = (data.companyPolicyViolations || []).map((v) => v.violationDescription || v.message || v);
        setComplianceViolations([
          ...(data.violations || []),
          ...hard,
          ...partIii,
          ...company
        ].filter(Boolean));
        setComplianceWarnings(data.warnings || []);
        setComplianceContext(data.complianceContext || null);
        setPartIiiEligible(!!data.partIiiEligible);
        setRequiredCompensatoryRestHours(data.requiredCompensatoryRestHours || 0);
        setHardBlockMessages(hard);
        if (data.requiredCompensatoryRestHours) {
          setFormData((prev) => ({
            ...prev,
            compensatoryRestHours: prev.compensatoryRestHours || data.requiredCompensatoryRestHours
          }));
        }
        setOverrideDialogOpen(true);
      } else if (error.response?.status === 400 && (data?.hardViolations || []).length > 0) {
        const msgs = data.hardViolations.map((v) => v.message || v).join(' ');
        showToast(data.message || msgs || 'DGCA compliance hard-block', 'error');
      } else {
        let errorMessage = 'Failed to save duty roster entry';
        if (data?.message) {
          errorMessage = data.message;
        } else if (data?.errors && Array.isArray(data.errors)) {
          errorMessage = data.errors.map(err => err.msg || err.message || err).join(', ');
        }
        console.error('Error saving duty roster entry:', data || error.message);
        showToast(errorMessage, 'error');
      }
    }
  };

  const handleOverride = async () => {
    if (!formData.nonComplianceJustification || formData.nonComplianceJustification.length < 50) {
      showToast('Please provide a justification of at least 50 characters', 'error');
      return;
    }
    if (partIiiEligible && requiredCompensatoryRestHours > 0) {
      const comp = Number(formData.compensatoryRestHours);
      if (!(comp >= requiredCompensatoryRestHours)) {
        showToast(`Compensatory rest of at least ${requiredCompensatoryRestHours}h is required for Part III extension`, 'error');
        return;
      }
    }

    // Validate dates
    if (!formData.dutyStartTime || !formData.dutyStartTime.isValid()) {
      showToast('Invalid duty start time', 'error');
      return;
    }
    if (!formData.dutyEndTime || !formData.dutyEndTime.isValid()) {
      showToast('Invalid duty end time', 'error');
      return;
    }

    if (formData.dutyType === 'FLIGHT' && formData.rosterPlanOnly && !(formData.dutyWorkLocationCity || '').trim()) {
      showToast('Work-Duty Location (city) is required for roster plan flight entries', 'error');
      return;
    }

    if (formData.isSplitDuty) {
      if (!formData.splitDutyBreakStart?.isValid?.() || !formData.splitDutyBreakEnd?.isValid?.()) {
        showToast('Split duty requires both break start and break end times', 'error');
        return;
      }
      if (!formData.splitDutyBreakEnd.isAfter(formData.splitDutyBreakStart)) {
        showToast('Split duty break end must be after break start', 'error');
        return;
      }
      if (
        formData.splitDutyBreakStart.isBefore(formData.dutyStartTime) ||
        formData.splitDutyBreakEnd.isAfter(formData.dutyEndTime)
      ) {
        showToast('Split duty break must fall within the duty period', 'error');
        return;
      }
    }

    const toUTC = (d) => {
      if (!d || !d.isValid()) return undefined;
      return displayDateTimeToUTC(d.format('YYYY-MM-DD'), d.format('HH:mm'), displayTimezone) || undefined;
    };

    try {
      const dutyStartTimeISO = toUTC(formData.dutyStartTime);
      const dutyEndTimeISO = toUTC(formData.dutyEndTime);
      if (!dutyStartTimeISO || !dutyEndTimeISO) {
        showToast('Invalid duty start or end time', 'error');
        return;
      }

      const payload = {
        crewId: formData.crewId,
        dutyType: formData.dutyType,
        dutyStartTime: dutyStartTimeISO,
        dutyEndTime: dutyEndTimeISO,
        remarks: formData.remarks,
        nonComplianceJustification: formData.nonComplianceJustification,
        compensatoryRestHours: formData.compensatoryRestHours !== '' && formData.compensatoryRestHours != null
          ? Number(formData.compensatoryRestHours)
          : undefined,
        restFacility: formData.restFacility || undefined,
        flightTimeTable: formData.flightTimeTable || undefined,
        recordedFlightTimeHours: formData.recordedFlightTimeHours !== '' && formData.recordedFlightTimeHours != null
          ? Number(formData.recordedFlightTimeHours)
          : undefined,
        aircraftType: aircraftType || undefined,
        trainingCategory: formData.dutyType === 'TRAINING' ? formData.trainingCategory : undefined,
        trainingAircraftId: formData.dutyType === 'TRAINING' && formData.trainingCategory === 'FLIGHT_TRAINING' && formData.trainingAircraftId ? formData.trainingAircraftId : undefined,
        sectorId: formData.sectorId || null,
        tripId: formData.tripId || null,
        crewComplement: formData.crewComplement || undefined,
        numberOfLandings: formData.numberOfLandings != null ? formData.numberOfLandings : undefined,
        hasPositioning: formData.hasPositioning || undefined,
        positioningStartTime: formData.hasPositioning ? toUTC(formData.positioningStartTime) : undefined,
        positioningEndTime: formData.hasPositioning ? toUTC(formData.positioningEndTime) : undefined,
        isSplitDuty: formData.isSplitDuty === true,
        splitDutyBreakStart: formData.isSplitDuty ? toUTC(formData.splitDutyBreakStart) : undefined,
        splitDutyBreakEnd: formData.isSplitDuty ? toUTC(formData.splitDutyBreakEnd) : undefined,
        splitDutyBreakDuration: formData.isSplitDuty && formData.splitDutyBreakStart && formData.splitDutyBreakEnd
          ? formData.splitDutyBreakEnd.diff(formData.splitDutyBreakStart, 'minute') / 60
          : undefined,
        splitDutyAccommodationProvided: formData.splitDutyAccommodationProvided || undefined,
        rosterPlanOnly: formData.dutyType === 'FLIGHT' ? formData.rosterPlanOnly === true : undefined,
        ...(formData.dutyType === 'FLIGHT' && formData.rosterPlanOnly
          ? {
              dutyWorkLocationCity: (formData.dutyWorkLocationCity || '').trim(),
              dutyWorkLocationCode: (() => {
                const c = (formData.dutyWorkLocationCode || '').trim().toUpperCase().replace(/[^A-Z]/g, '');
                return c.slice(0, 4) || undefined;
              })()
            }
          : {})
      };

      const toastOffDayWarnings = (warnings) => {
        const offDayMsgs = (warnings || [])
          .filter((w) => w?.type === 'OFF_DAY_PATTERN_WARNING' || /off day/i.test(w?.message || w || ''))
          .map((w) => w.message || w);
        if (offDayMsgs.length) {
          showToast(offDayMsgs.join(' '), 'warning');
        }
      };

      if (selectedEntry) {
        const response = await axios.put(`${API_URL}/ops/crew-duty-roster/${selectedEntry._id}`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        showToast('Duty roster entry updated with Part III / policy justification', 'success');
        toastOffDayWarnings(response.data?.warnings);
      } else {
        const response = await axios.post(`${API_URL}/ops/crew-duty-roster`, payload, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
        showToast('Duty roster entry created with Part III / policy justification', 'success');
        toastOffDayWarnings(response.data?.warnings);
      }

      setDialogOpen(false);
      setOverrideDialogOpen(false);
      fetchEntries();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to save duty roster entry', 'error');
    }
  };

  const handleDelete = async (entry) => {
    if (!window.confirm('Are you sure you want to cancel this duty assignment?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/ops/crew-duty-roster/${entry._id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      showToast('Duty roster entry cancelled', 'success');
      fetchEntries();
    } catch (error) {
      showToast('Failed to cancel duty roster entry', 'error');
    }
  };

  const handleApplyTechlogProposed = async (entry) => {
    try {
      await axios.post(
        `${API_URL}/ops/crew-duty-roster/${entry._id}/apply-techlog-proposed`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      showToast('Applied techlog proposed times', 'success');
      fetchEntries();
    } catch (error) {
      showToast(error.response?.data?.message || 'Failed to apply techlog times', 'error');
    }
  };

  return {
    dialogOpen,
    setDialogOpen,
    selectedEntry,
    formData,
    setFormData,
    aircraftType,
    setAircraftType,
    complianceWarnings,
    setComplianceWarnings,
    complianceViolations,
    setComplianceViolations,
    complianceContext,
    setComplianceContext,
    partIiiEligible,
    requiredCompensatoryRestHours,
    hardBlockMessages,
    overrideDialogOpen,
    setOverrideDialogOpen,
    handleCreate,
    handleCreateFromMismatch,
    handleEdit,
    handleViewDutyFromCalendar,
    handleSave,
    handleOverride,
    handleDelete,
    handleApplyTechlogProposed
  };
};
